import gsap from "gsap";
import { ScrollTrigger } from "gsap/ScrollTrigger";
import Lenis from "lenis";

gsap.registerPlugin(ScrollTrigger);

let lenisInstance: Lenis | null = null;
let tickerCallback: ((time: number) => void) | null = null;

export const DESKTOP_MQ = "(min-width: 768px)";

export function prefersReducedMotion(): boolean {
  if (typeof window === "undefined") return true;
  return window.matchMedia("(prefers-reduced-motion: reduce)").matches;
}

export function isDesktop(): boolean {
  if (typeof window === "undefined") return false;
  return window.matchMedia(DESKTOP_MQ).matches;
}

export function canUseCinematicMotion(): boolean {
  return !prefersReducedMotion();
}

export function canUsePinScrub(): boolean {
  return canUseCinematicMotion() && isDesktop();
}

export function getLenis(): Lenis | null {
  return lenisInstance;
}

export function initLenis(): Lenis | null {
  if (typeof window === "undefined") return null;
  if (prefersReducedMotion()) return null;
  if (lenisInstance) return lenisInstance;

  lenisInstance = new Lenis({
    duration: 1.1,
    easing: (t) => Math.min(1, 1.001 - Math.pow(2, -10 * t)),
    smoothWheel: true,
  });

  // Avoid double-smoothing with CSS scroll-behavior
  document.documentElement.style.scrollBehavior = "auto";

  lenisInstance.on("scroll", ScrollTrigger.update);

  tickerCallback = (time: number) => {
    lenisInstance?.raf(time * 1000);
  };
  gsap.ticker.add(tickerCallback);
  gsap.ticker.lagSmoothing(0);

  return lenisInstance;
}

export function destroyLenis(): void {
  if (tickerCallback) {
    gsap.ticker.remove(tickerCallback);
    tickerCallback = null;
  }
  if (lenisInstance) {
    lenisInstance.destroy();
    lenisInstance = null;
  }
  if (typeof document !== "undefined") {
    document.documentElement.style.scrollBehavior = "";
  }
}

export function refreshScrollTrigger(): void {
  ScrollTrigger.refresh();
}

export function killScrollTriggers(): void {
  ScrollTrigger.getAll().forEach((t) => t.kill());
}

export function scrollToElement(
  target: string | HTMLElement,
  options?: { offset?: number }
): void {
  const el =
    typeof target === "string" ? document.querySelector(target) : target;
  if (!el) return;

  const offset = options?.offset ?? -80;

  if (lenisInstance && !prefersReducedMotion()) {
    lenisInstance.scrollTo(el as HTMLElement, { offset });
  } else {
    (el as HTMLElement).scrollIntoView({ behavior: "smooth", block: "start" });
  }
}

export { gsap, ScrollTrigger };
